import React, { useState, useRef, useEffect } from 'react'
import { useRequest } from '../../hooks/UseRequest'
import { useLeadsWithLoans } from '../../hooks/UseLeadsWithLoans'
import { selectAuth } from "../../redux/authSlice"
import { setModal, selectModal } from "../../redux/modalSlice"
import { useDispatch, useSelector } from 'react-redux'
import once from 'lodash/once'
import { useParams } from "react-router-dom"
import Loading from '../../components/Loading'
import Select from "react-select"
import { setNotification } from '../../redux/notificationSlice'
import { selectDropdownOptions } from "../../redux/dropdownSlice";

// Froala
import FroalaEditorComponent from 'react-froala-wysiwyg'
import FroalaEditor from 'froala-editor'
import "froala-editor/js/froala_editor.pkgd.min.js"
import "froala-editor/js/plugins/lists.min"
import "froala-editor/js/plugins.pkgd.min.js"
import "froala-editor/js/third_party/embedly.min.js"

// Require Editor CSS files.
import "froala-editor/css/froala_style.min.css"
import "froala-editor/css/froala_editor.pkgd.min.css"
import "froala-editor/css/third_party/embedly.min.css"

export default function AddNoteModal() {
  const dispatch = useDispatch()
  const requests = useRequest()
  const auth = useSelector(selectAuth)
  const modal = useSelector(selectModal)
  const params = useParams()
  const dropdownOptions = useSelector(selectDropdownOptions);

  const noteTagType = dropdownOptions.filter((o) => o.code_key === 'tag_type_note')[0]
  const customerTagType = dropdownOptions.filter((o) => o.code_key === 'tag_type_customer')[0]
  const activityCodeType = dropdownOptions.filter((o) => o.code_key === 'tag_type_activity_code')[0]
  const resultCodeType = dropdownOptions.filter((o) => o.code_key === 'tag_type_result_code')[0]
  const noteTypeRep = dropdownOptions.filter((o) => o.code_key === 'note_type_rep')[0]
  const requestingTag = useRef(false)
  const requestingLeads = useRef(false)
  let addNoteEditorRef = useRef(false)
  const { leadData, getLeadsWithLoans } = useLeadsWithLoans()

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      requestingTag.current = true
      getCustomerTag()
      // Skip only a duplicate request for the same page — a new customer must not wait on the old one.
      if (requestingLeads.current !== (params.id ?? null)) {
        const requestedId = params.id ?? null
        requestingLeads.current = requestedId
        getLeadsWithLoans().finally(() => {
          if (requestingLeads.current === requestedId) requestingLeads.current = false
        })
      }
    }
  }, [auth, params.id])

  const [customerTag, setCustomerTag] = useState([])
  const [errors, setErrors] = useState({});

  // Local State
  const [loadedNote, setLoadedNote] = useState(false)
  const [content, setContent] = useState('')
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedResultCode, setSelectedResultCode] = useState(null)
  const [selectedActivityCode, setSelectedActivityCode] = useState(null)
  const [loading, setLoading] = useState(false)
  const [flag, setFlag] = useState(false);
  const [optionTags, setOptionTags] = useState(false);
  const [loanData, setLoanData] = useState(null)
  const [selectedLead, setSelectedLead] = useState(null)
  const [selectedLoan, setSelectedLoan] = useState(null)

  useEffect(() => {
    if (flag) {
      doValidation(addNoteEditorRef.editor.html.get());
    }
  }, [selectedResultCode, selectedTags, selectedActivityCode])

  const doValidation = (noteContent) => {
    const validationErrors = {};

    if (!selectedResultCode) {
      validationErrors.resultCode = "Please select a Disposition"
    }
    if (!selectedActivityCode) {
      validationErrors.activityCode = "Please select a Activity Code"
    }

    if (noteContent?.trim() === '') {
      validationErrors.content = "Content should be filled out"
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    } else {
      setErrors({})
      return true
    }
  }

  const handleSaveNote = async () => {
    const noteContent = addNoteEditorRef.editor?.html.get()
    if (!doValidation(noteContent)) {
      setFlag(true);
      return;
    }
    setLoading(true)

    requests.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/customer_note`
    )

    try {
      let body = {
        body: noteContent,
        result_code_tag_id: selectedResultCode.value,
        activity_code_tag_id: selectedActivityCode.value,
        tags: selectedTags ? selectedTags?.map((x) => parseInt(x.value, 10)) : [],
        customer_id: params.id,
        note_type_dropdown_option_id: noteTypeRep.id,
        lead_id: selectedLead?.value ?? null,
        loan_id: selectedLoan?.value ?? null,
      }

      const response = await requests.post(body);
      if (response.status === 201) {
        dispatch(setNotification({ type: 'success', body: 'Successfully Saved.' }))
        resetStates();
        addNoteEditorRef?.editor?.html?.set('');
        dispatch(setModal({ is_active: false, options: { refresh: 'NOTE_DASHLET' } }))
      }
    } catch (err) {
      resetStates();
      addNoteEditorRef?.editor?.html?.set('');
      dispatch(setModal({ is_active: false }))
      dispatch(setNotification({ type: 'error', body: err.response?.data?.message }))
    }
    setLoading(false);
  };

  const handleClose = () => {
    resetStates()
    dispatch(setModal({ title: ``, content: ``, type: '', is_active: false }))
  }

  const resetStates = () => {
    setSelectedActivityCode(null)
    setSelectedResultCode(null)
    setSelectedTags([])
    setContent('')
    setFlag(false);
    setErrors({})
    setSelectedLead(null)
    setSelectedLoan(null)
    setLoanData(null)
  }

  useEffect(() => {
    if (selectedResultCode && selectedActivityCode) {
      setOptionTags(customerTag.filter((x) => selectedResultCode?.relatedTags?.includes(x.value)))
    }
  }, [selectedResultCode, selectedActivityCode])

  const getCustomerTag = async () => {
    try {
      // this endpoint is returning all data, no pagination
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/tag`)
      const response = await requests.get()
      setCustomerTag(response.data.data.map((x) => ({ value: x.id, label: x.label, ddo: x.tag_type_dropdown_option_id, relatedTags: x.tag_related_tags.map((item) => item.related_tag_id) })))
      setLoadedNote(true)
    } catch (err) {
      dispatch(setNotification({ type: 'error', body: err.response?.data?.message }))
    } finally {
      requestingTag.current = false
    }
  }

  const initializeOnce = once(({ initialize }) => initialize())

  return modal.is_active && modal.type === 'add_note' ? (
    <div className="modal is-active is-large">
      <div className="modal-background"></div>
      <div className="modal-card column is-four-fifths">
        <header className="modal-card-head py-3">
          <p className="modal-card-title is-capitalized">
            Add Note
          </p>
          <button className="delete" aria-label="close" onClick={handleClose}></button>
        </header>
        <section className="modal-card-body lox-footer-modal-card-body pt-1 pb-1">
          {!loadedNote && <Loading />}
          {loadedNote && (
            <>
              <div className="columns pt-3 px-1">
                <div className="column py-1">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Activity Code </span><span className="has-text-danger">*</span>
                  </p>
                  <Select
                    name="activityCode"
                    options={customerTag.filter((x) => x.ddo === activityCodeType.id)}
                    value={selectedActivityCode}
                    onChange={(e) => {
                      setSelectedActivityCode(e)
                      setSelectedResultCode(null)
                      setSelectedTags(null)
                    }}
                    placeholder="Search a Activity Code"
                    styles={{ menu: (provided) => ({ ...provided, zIndex: 100 }) }}
                  />
                  {errors?.activityCode && (
                    <p className="has-text-danger">{errors?.activityCode}</p>
                  )}
                </div>
              </div>
              <div className="columns px-1">
                <div className="column py-1">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Disposition </span><span className="has-text-danger">*</span>
                  </p>
                  <Select
                    name="resultCode"
                    options={customerTag.filter((x) => x.ddo === resultCodeType.id && selectedActivityCode?.relatedTags?.includes(x.value))}
                    value={selectedResultCode}
                    isDisabled={!selectedActivityCode}
                    onChange={(e) => {
                      setSelectedResultCode(e)
                      setSelectedTags(null)
                    }}
                    placeholder="Search a Disposition"
                    styles={{ menu: (provided) => ({ ...provided, zIndex: 100 }) }}
                  />
                  {errors?.resultCode && (
                    <p className="has-text-danger">{errors?.resultCode}</p>
                  )}
                </div>
              </div>
              <div className="columns px-1">
                <div className="column py-1">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Tag </span>
                  </p>
                  <Select
                    isMulti
                    name="tags"
                    options={optionTags}
                    value={selectedTags}
                    onChange={(e) => setSelectedTags(e)}
                    isDisabled={!selectedActivityCode || !selectedResultCode || optionTags?.length === 0}
                    placeholder={optionTags?.length === 0 && selectedResultCode ? selectedResultCode?.label + " has no tag" : "Search Tags ..."}
                    styles={{ menu: (provided) => ({ ...provided, zIndex: 100 }) }}
                  />
                  {errors?.tag && (
                    <p className="has-text-danger">{errors?.tag}</p>
                  )}
                </div>
              </div>
              <div className="columns px-1">
                <div className="column py-1">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Lead </span>
                  </p>
                  <div data-testid="add-note-modal-lead-select">
                    <Select
                      name="lead_id"
                      isClearable
                      isLoading={leadData === null}
                      options={leadData ?? []}
                      value={selectedLead}
                      onChange={(e) => {
                        setSelectedLead(e)
                        setSelectedLoan(null)
                        setLoanData(e?.loans ?? [])
                      }}
                      placeholder="Search a Lead"
                      styles={{ menu: (provided) => ({ ...provided, zIndex: 100 }) }}
                    />
                  </div>
                </div>
                <div className="column py-1">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Loan </span>
                  </p>
                  <div data-testid="add-note-modal-loan-select">
                    <Select
                      name="loan_id"
                      isClearable
                      options={loanData ?? []}
                      value={selectedLoan}
                      isDisabled={!selectedLead}
                      onChange={(e) => setSelectedLoan(e)}
                      placeholder="Search a Loan"
                      styles={{ menu: (provided) => ({ ...provided, zIndex: 100 }) }}
                    />
                  </div>
                </div>
              </div>
              <div className="columns px-1">
                <div className="column py-1">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Content </span><span className="has-text-danger">*</span>
                  </p>
                  <FroalaEditorComponent
                    ref={(ref) => (addNoteEditorRef = ref)}
                    onModelChange={() => (setContent(addNoteEditorRef.editor.html.get()))}
                    tag="textarea"
                    model={content}
                    onManualControllerReady={initializeOnce}
                    style={{ height: '200px', width: '100%' }}
                    config={{
                      key: 'Ja2A4wB1A1B1E2A4H4D3B2C1pdzdNSWXa1c1MDe1CI1PLPFa1F1EESFKVlA6F6D5C4A1D3A10A3A5A3',
                      attribution: false,
                      enter: FroalaEditor.ENTER_BR,
                      htmlUntouched: true,
                      toolbarButtons: {
                        moreText: {
                          buttons: [
                            "bold",
                            "italic",
                            "underline",
                          ]
                        },
                        moreParagraph: {
                          buttons: [
                            "fontSize",
                            "textColor",
                            "formatOL",
                            "formatUL",
                          ],
                          buttonsVisible: 4
                        },
                      },
                      events: {
                        // eslint-disable-next-line func-names
                        blur: function () {
                          // eslint-disable-next-line react/no-this-in-sfc
                          this.selection.save()
                        },
                      },
                    }}
                  />
                  {errors?.content && (
                    <p className="has-text-danger">{errors?.content}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
        <footer className="modal-card-foot is-justify-content-end py-1">
          <div className="lox-form__buttons-wrapper has-text-right">
            <button
              type="button"
              onClick={handleClose}
              className="button is-small has-background-grey has-text-white"
            >
              Cancel
            </button>
            {loading ? (
              <button className="button is-link is-small p-2" disabled>
                <Loading size="is-size-5" />  Save Note
              </button>
            ) : (
              <button className="button is-link is-small p-2" onClick={handleSaveNote}>
                Save Note
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  ) : ''
}
